/**
 * ProductSalesMaterialsEditor の title / URL inline table 操作ロジック。
 *
 * component から切り出した純粋関数群。副作用なし・framework 非依存。
 * `replaceMaterial` は store.update() 内に吸収済みのため本モジュールには含まない。
 */
import { ApiError } from "@shared/api/client";
import type {
  ProductSalesMaterial,
  ProductSalesMaterialCreateRequest,
  ProductSalesMaterialUpdateRequest,
} from "@shared/types/product-sales-material";
import { createUrl } from "@shared/value-objects";

export interface MaterialDraft {
  title: string;
  url: string;
  usage_scene: string;
}

/** 空の下書きを生成する。 */
export function createEmptyDraft(): MaterialDraft {
  return { title: "", url: "", usage_scene: "" };
}

/** 既存資料から編集用下書きを生成する。 */
export function draftFromMaterial(
  material: ProductSalesMaterial,
): MaterialDraft {
  return {
    title: material.title,
    url: material.url,
    usage_scene: material.notes ?? "",
  };
}

/** title / URL がともに空の入力行かどうか。 */
export function isBlankDraft(draft: MaterialDraft): boolean {
  return (
    draft.title.trim().length === 0 &&
    draft.url.trim().length === 0 &&
    draft.usage_scene.trim().length === 0
  );
}

/** 保存対象になる title / URL がそろった行かどうか。 */
export function isCompleteDraft(draft: MaterialDraft): boolean {
  return draft.title.trim().length > 0 && draft.url.trim().length > 0;
}

/** バリデーション。エラーがあれば文字列、なければ null。 */
export function validateDraft(draft: MaterialDraft): string | null {
  if (draft.title.trim().length === 0 && draft.url.trim().length === 0) {
    return null;
  }
  if (!isCompleteDraft(draft)) {
    return "タイトルとURLの両方が入力された行のみ保存されます";
  }
  // backend domain (core::value_objects::Url) と同一ポリシー。ここで弾かないと
  // 保存は 400 で失敗する。無効な保存済み URL はリンクにせずテキスト表示になる。
  if (!createUrl(draft.url.trim())) {
    return "URL は http:// または https:// で始まる有効なURLを入力してください";
  }
  return null;
}

function normalizedUsageScene(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** 新規作成用 payload を組み立てる。利用場面は任意自由記述として notes に保存する。 */
export function buildCreatePayload(
  draft: MaterialDraft,
  sortOrder?: number,
): ProductSalesMaterialCreateRequest {
  return {
    title: draft.title.trim(),
    url: draft.url.trim(),
    // material_type は DB 制約維持用。UI では利用場面を notes の自由記述として扱う。
    material_type: "service_deck",
    notes: normalizedUsageScene(draft.usage_scene),
    ...(sortOrder === undefined ? {} : { sort_order: sortOrder }),
  };
}

/** 既存資料の最大 sort_order の次を返す。 */
export function nextSortOrder(
  materials: readonly Pick<ProductSalesMaterial, "sort_order">[],
): number {
  return (
    materials.reduce(
      (max, material) => Math.max(max, material.sort_order),
      -1,
    ) + 1
  );
}

/** PATCH 用の差分 payload を組み立てる。変更のないフィールドは含まない。 */
export function buildPatchPayload(
  current: ProductSalesMaterial,
  draft: MaterialDraft,
): ProductSalesMaterialUpdateRequest {
  const patch: ProductSalesMaterialUpdateRequest = {};
  const title = draft.title.trim();
  const url = draft.url.trim();
  const usageScene = normalizedUsageScene(draft.usage_scene);

  if (title !== current.title) patch.title = title;
  if (url !== current.url) patch.url = url;
  if (usageScene !== current.notes) patch.notes = usageScene;

  return patch;
}

/** エラーを表示用文字列に変換する。ApiError は message をそのまま使う。 */
export function formatError(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return fallback;
}
